import { FC, useCallback } from 'react';
import { API, OutputData } from '@editorjs/editorjs';
import { useFragment } from 'react-relay';
import { useField } from 'formik';
import { createReactEditorJS } from 'react-editor-js';
import List from '@editorjs/list';
import Header from '@editorjs/header';
import graphql from 'babel-plugin-relay/macro';

import Typography from '@mui/material/Typography';

import { useEditor } from 'components/EditorJsField';
import { WrapperProps, EditorCore } from '@react-editor-js/core';
import { EditorLanguageField_language$key } from '__generated__/EditorLanguageField_language.graphql';

type Props = {
  language: EditorLanguageField_language$key;
  fromHtml?: string | null;
} & Omit<WrapperProps, 'tools' | 'minHeight' | 'onChange'>;

const ReactEditorJS = createReactEditorJS();

const tools = {
  list: List,
  header: Header,
};

const EditorLanguageField: FC<Props> = props => {
  const { fromHtml, ...rest } = props;
  const { name, selectedLocale, register, editorCore } = useEditor();

  const language = useFragment(
    graphql`
      fragment EditorLanguageField_language on Language {
        locale
        readOnly
      }
    `,
    props.language
  );

  const [{ value }, , { setValue }] = useField<OutputData>(
    `${name}.${language.locale}`
  );

  const handleInitialize = useCallback(
    (instance: EditorCore) => {
      register(language.locale, instance);
    },
    [language.locale, register]
  );

  const handleChange = useCallback(
    async (api: API) => {
      setValue(await api.saver.save());
    },
    [setValue]
  );

  const handleReady = useCallback(async () => {
    if (fromHtml) {
      const editor = editorCore(language.locale);

      if (!editor) return;

      editor.dangerouslyLowLevelInstance.blocks.renderFromHTML(fromHtml);
      setValue(await editor.save());
    }
  }, [editorCore, fromHtml, language.locale, setValue]);

  const htmlId = `editor_${name}.${language.locale}`;

  return (
    <Typography component="div" hidden={language.locale !== selectedLocale}>
      <ReactEditorJS
        holder={htmlId}
        onChange={handleChange}
        tools={tools}
        minHeight={50}
        defaultValue={value}
        onInitialize={handleInitialize}
        onReady={handleReady}
        readOnly={language.readOnly}
        {...rest}>
        <div id={htmlId} />
      </ReactEditorJS>
    </Typography>
  );
};

export default EditorLanguageField;
